import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User,
  Mail,
  Building2,
  Calendar,
  Globe,
  Clock,
  Banknote,
  FlaskConical,
  MapPin,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { useDesignSystem } from "@/contexts/DesignSystemContext";
import { Switch } from "@/components/ui/switch";
import { SWISS_CANTONS } from "@/components/SmartCompanyInput";
import { toast } from "sonner";

export default function UserProfile() {
  return (
    <DashboardLayout>
      <UserProfileContent />
    </DashboardLayout>
  );
}

function UserProfileContent() {
  const { user } = useAuth();
  const { selectedCompanyId } = useCompany();
  const { themeKey, source, isLoading: themeLoading } = useDesignSystem();
  const utils = trpc.useUtils();

  // User settings (sandbox mode, canton)
  const { data: userSettings, isLoading: settingsLoading } =
    trpc.userSettings.get.useQuery();
  const updateSettings = trpc.userSettings.update.useMutation({
    onSuccess: () => {
      utils.userSettings.get.invalidate();
      toast.success("Settings updated");
    },
    onError: error => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
  });
  const setUserTheme = trpc.designPreferences.setUserPreference.useMutation({
    onSuccess: () => {
      utils.designPreferences.resolve.invalidate({
        companyId: selectedCompanyId,
      });
      toast.success("Preferences saved successfully");
    },
    onError: error => {
      toast.error(`Failed to save preferences: ${error.message}`);
    },
  });

  // Get current preferences from overrides
  const resolvedPrefs = trpc.designPreferences.resolve.useQuery({
    companyId: selectedCompanyId,
  });
  const currentOverrides = resolvedPrefs.data?.user?.overrides as
    | Record<string, string>
    | undefined;

  // Language preference
  const currentLanguage = currentOverrides?.language || "en";

  // Timezone preference
  const currentTimezone = currentOverrides?.timezone || "Europe/Zurich";

  // Currency preference
  const currentCurrency = currentOverrides?.currency || "CHF";

  const updatePreference = (key: string, value: string) => {
    const newOverrides = {
      ...currentOverrides,
      [key]: value,
    };
    setUserTheme.mutate({
      themeKey: themeKey,
      companyId: selectedCompanyId,
      overrides: newOverrides,
    });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">
          Please log in to view your profile
        </p>
      </div>
    );
  }

  const initials =
    user.name
      ?.split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase() || "U";

  return (
    <div className="container py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <User className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">User Profile</h1>
          <p className="text-muted-foreground">
            Manage your account information and preferences
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Profile Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6 mb-6">
              <Avatar className="h-20 w-20">
                <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-semibold">{user.name}</h2>
                <p className="text-muted-foreground">{user.email}</p>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <User className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Full Name</p>
                  <p className="text-sm text-muted-foreground">{user.name}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Email Address</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Role</p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {user.role}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Account Created</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Account Settings</CardTitle>
            <CardDescription>Manage your account preferences</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
                <div>
                  <Label>Design theme</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Tiered resolution: {source} → {themeKey} (tenant + user
                    chain)
                  </p>
                </div>
                <select
                  className="border rounded-md px-3 py-2 bg-background text-foreground"
                  value={themeKey}
                  disabled={themeLoading || setUserTheme.isPending}
                  onChange={e =>
                    setUserTheme.mutate({
                      themeKey: e.target.value,
                      companyId: selectedCompanyId,
                    })
                  }
                >
                  <option value="light">Light (default)</option>
                  <option value="dark">Dark</option>
                  <option value="sakura">Sakura (cultural)</option>
                  <option value="andes">Andes (cultural)</option>
                </select>
              </div>

              <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label>Language Preference</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Choose your preferred language
                    </p>
                  </div>
                </div>
                <Select
                  value={currentLanguage}
                  disabled={setUserTheme.isPending || resolvedPrefs.isLoading}
                  onValueChange={value => updatePreference("language", value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label>Time Zone</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your local time zone for dates and times
                    </p>
                  </div>
                </div>
                <Select
                  value={currentTimezone}
                  disabled={setUserTheme.isPending || resolvedPrefs.isLoading}
                  onValueChange={value => updatePreference("timezone", value)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Europe/Zurich">
                      Europe/Zurich (CET)
                    </SelectItem>
                    <SelectItem value="Europe/Berlin">
                      Europe/Berlin (CET)
                    </SelectItem>
                    <SelectItem value="Europe/Paris">
                      Europe/Paris (CET)
                    </SelectItem>
                    <SelectItem value="Europe/London">
                      Europe/London (GMT)
                    </SelectItem>
                    <SelectItem value="America/New_York">
                      America/New York (EST)
                    </SelectItem>
                    <SelectItem value="Asia/Singapore">
                      Asia/Singapore (SGT)
                    </SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Banknote className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label>Currency</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Default currency for financial display
                    </p>
                  </div>
                </div>
                <Select
                  value={currentCurrency}
                  disabled={setUserTheme.isPending || resolvedPrefs.isLoading}
                  onValueChange={value => updatePreference("currency", value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHF">Swiss Franc (CHF)</SelectItem>
                    <SelectItem value="EUR">Euro (EUR)</SelectItem>
                    <SelectItem value="USD">US Dollar (USD)</SelectItem>
                    <SelectItem value="GBP">British Pound (GBP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label>Default Canton</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Pre-selected canton for company lookups
                    </p>
                  </div>
                </div>
                <Select
                  value={userSettings?.lastUsedCanton ?? "ZH"}
                  disabled={updateSettings.isPending || settingsLoading}
                  onValueChange={value =>
                    updateSettings.mutate({ lastUsedCanton: value })
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select canton" />
                  </SelectTrigger>
                  <SelectContent>
                    {SWISS_CANTONS.map(canton => (
                      <SelectItem key={canton.code} value={canton.code}>
                        {canton.code} - {canton.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4 p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <FlaskConical className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label>Sandbox Mode</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Enable hard delete and test features for trial accounts
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={userSettings?.sandboxMode ?? false}
                    disabled={updateSettings.isPending || settingsLoading}
                    onCheckedChange={checked =>
                      updateSettings.mutate({ sandboxMode: checked })
                    }
                  />
                  <span className="text-sm text-muted-foreground">
                    {userSettings?.sandboxMode ? "On" : "Off"}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-muted-foreground">
                    Add an extra layer of security to your account
                  </p>
                </div>
                <Button variant="outline" disabled>
                  Coming Soon
                </Button>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Active Sessions</p>
                  <p className="text-sm text-muted-foreground">
                    Manage devices where you're logged in
                  </p>
                </div>
                <Button variant="outline" disabled>
                  Coming Soon
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
